import { Loader2, ClipboardList, Boxes } from "lucide-react";
import { useCreatePanneau } from "../api/queries";

interface Props {
  conv: string;
  organisationName: string;
}

/**
 * Shown when the conversation has no open panneaux yet.
 *
 * Phase 2 offers two starter shapes:
 * - Commande sortante for Jojo & Co (dated today) — pastry/coffee orders
 * - Inventaire — current stock for the 10 boutique cafés
 */
export function EmptyState({ conv, organisationName }: Props) {
  const createPanneau = useCreatePanneau();
  // conv + organisation are kept on the create body for backwards compat
  // with the test stub fixtures, but the server derives them from the
  // session and ignores client-supplied values now.
  void conv;
  void organisationName;

  const createJojo = () => {
    createPanneau.mutate({
      name: "Jojo & Co",
      kind: "commande_sortante",
      // Client-only filter; per-row delivery_date is shown in the
      // tableau so users can see when each line is for, without the
      // panneau silently hiding rows whose date doesn't match.
      config: { client: "Jojo & Co" },
    });
  };

  const createInventaire = () => {
    createPanneau.mutate({
      name: "Stock cafés boutique",
      kind: "inventaire",
      config: {
        // The 10 boutique coffees from Source/Stock cafés boutique.xlsx.
        canonical_ids: [
          "cafe_chelchele",
          "cafe_gatare",
          "cafe_tojtik_bio",
          "cafe_sunda",
          "cafe_buna_bet",
          "cafe_el_guamo",
          "cafe_bombe",
          "cafe_el_placer",
          "cafe_guarani",
          "cafe_deca_choco",
        ],
      },
    });
  };

  const pending = createPanneau.isPending;

  return (
    <section className="rounded-2xl border border-dashed border-black/15 dark:border-white/15 bg-white dark:bg-surface-dark p-10 flex flex-col items-center gap-5 text-center">
      <h2 className="text-base font-semibold">Aucun panneau ouvert</h2>
      <p className="text-sm text-muted dark:text-muted-dark max-w-md">
        Créez un panneau pour commencer.
      </p>
      <div className="flex flex-wrap items-center gap-3 justify-center">
        <button
          type="button"
          onClick={createJojo}
          disabled={pending}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-ink text-white text-sm font-semibold hover:bg-ink/90 disabled:opacity-50"
        >
          {pending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <ClipboardList className="h-4 w-4" />
          )}
          Commande Jojo &amp; Co
        </button>
        <button
          type="button"
          onClick={createInventaire}
          disabled={pending}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white dark:bg-surface-dark text-ink dark:text-ink-dark ring-1 ring-ink dark:ring-white text-sm font-semibold hover:bg-canvas dark:hover:bg-canvas-dark disabled:opacity-50"
        >
          {pending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Boxes className="h-4 w-4" />
          )}
          Inventaire stock cafés
        </button>
      </div>
      <p className="text-xs text-muted dark:text-muted-dark">
        Ou parlez directement à l'agent en haut de l'écran — un panneau
        adapté apparaîtra quand il créera des propositions.
      </p>
    </section>
  );
}
